import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { TrendingUp, Users, Target, RefreshCw, Building2, Globe, Lightbulb, BarChart3, Filter } from 'lucide-react';
import { 
  analyticsService, 
  AnalyticsOverview, 
  WorkshopPerformance, 
  FinancialAnalytics, 
  AnalyticsFilters,
  TeamPerformance,
  MentorPerformance,
  DepartmentBenchmark,
  PlantAnalytics,
  TeamCollaboration,
  PerformanceInsight,
  TemporalAnalytics,
  PredictiveAnalytics,
  SQDCMAnalytics,
  APSMaturity,
  DepartmentChartsData,
  TeamChartsData,
  PlantChartsData,
  InsightsChartsData
} from '../services/analytics';
import AnalyticsOverviewComponent from '../components/analytics/AnalyticsOverview';
import WorkshopPerformanceComponent from '../components/analytics/WorkshopPerformance';
import FinancialAnalyticsComponent from '../components/analytics/FinancialAnalytics';
import { MentorPerformance as MentorPerformanceComponent } from '../components/analytics/MentorPerformance';
import { DepartmentBenchmarking } from '../components/analytics/DepartmentBenchmarking';
import { TeamCollaboration as TeamCollaborationComponent } from '../components/analytics/TeamCollaboration';
import { PlantAnalytics as PlantAnalyticsComponent } from '../components/analytics/PlantAnalytics';
import { PerformanceInsights } from '../components/analytics/PerformanceInsights';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ChartErrorBoundary from '../components/common/ChartErrorBoundary';
// Phase 3 components
import TrendsChart from '../components/analytics/TrendsChart';
import RiskAssessmentGauge from '../components/analytics/RiskAssessmentGauge';
import SQDCMRadarChart from '../components/analytics/SQDCMRadarChart';
import MaturityProgressionChart from '../components/analytics/MaturityProgressionChart';
import PredictiveInsights from '../components/analytics/PredictiveInsights';
import SeasonalPatterns from '../components/analytics/SeasonalPatterns';
import LearningPaths from '../components/analytics/LearningPaths';
import ExportDashboard from '../components/analytics/ExportDashboard';
// New chart components for Overview tab
import CompletionRatesChart from '../components/analytics/CompletionRatesChart';
import StatusDistributionChart from '../components/analytics/StatusDistributionChart';
import ProgressVelocityChart from '../components/analytics/ProgressVelocityChart';
import DepartmentPerformanceChart from '../components/analytics/DepartmentPerformanceChart';
// Performance tab charts
import ProgressDistributionChart from '../components/analytics/ProgressDistributionChart';
import PerformanceByStatusChart from '../components/analytics/PerformanceByStatusChart';
import VelocityTrendChart from '../components/analytics/VelocityTrendChart';
import RiskDistributionChart from '../components/analytics/RiskDistributionChart';
import DepartmentPerformanceScatterChart from '../components/analytics/DepartmentPerformanceScatterChart';
import CategoryPerformanceChart from '../components/analytics/CategoryPerformanceChart';
// Department tab charts
import DepartmentPerformanceComparisonChart from '../components/analytics/DepartmentPerformanceComparisonChart';
import DepartmentSavingsAnalysisChart from '../components/analytics/DepartmentSavingsAnalysisChart';
import DepartmentProgressTrendsChart from '../components/analytics/DepartmentProgressTrendsChart';
import DepartmentWorkshopDistributionChart from '../components/analytics/DepartmentWorkshopDistributionChart';
import DepartmentRiskAssessmentChart from '../components/analytics/DepartmentRiskAssessmentChart';
import DepartmentMentorPerformanceChart from '../components/analytics/DepartmentMentorPerformanceChart';
// Team tab charts
import TeamPerformanceComparisonChart from '../components/analytics/TeamPerformanceComparisonChart';
import MentorEffectivenessAnalysisChart from '../components/analytics/MentorEffectivenessAnalysisChart';
import TeamPerformanceTrendsChart from '../components/analytics/TeamPerformanceTrendsChart';
import TeamWorkloadDistributionChart from '../components/analytics/TeamWorkloadDistributionChart';
import TeamKnowledgeSharingMatrixChart from '../components/analytics/TeamKnowledgeSharingMatrixChart';
import TeamCollaborationNetworkChart from '../components/analytics/TeamCollaborationNetworkChart';
// Plant tab charts
import PlantPerformanceComparisonChart from '../components/analytics/PlantPerformanceComparisonChart';
import PlantSavingsAnalysisChart from '../components/analytics/PlantSavingsAnalysisChart';
import PlantProgressTrendsChart from '../components/analytics/PlantProgressTrendsChart';
import PlantWorkshopDistributionChart from '../components/analytics/PlantWorkshopDistributionChart';
import PlantRiskAssessmentChart from '../components/analytics/PlantRiskAssessmentChart';
import PlantMentorPerformanceChart from '../components/analytics/PlantMentorPerformanceChart';
// Insights tab charts
import StrategicPerformanceDashboard from '../components/analytics/StrategicPerformanceDashboard';
import CrossDepartmentCollaborationMatrix from '../components/analytics/CrossDepartmentCollaborationMatrix';
import PredictiveAnalyticsDashboard from '../components/analytics/PredictiveAnalyticsDashboard';
import ROIBusinessImpactAnalysis from '../components/analytics/ROIBusinessImpactAnalysis';
import PerformanceBenchmarkingMatrix from '../components/analytics/PerformanceBenchmarkingMatrix';
import StrategicRecommendationsEngine from '../components/analytics/StrategicRecommendationsEngine';
// Financial tab charts
import SavingsTrendChart from '../components/analytics/SavingsTrendChart';
import ROIAnalysisChart from '../components/analytics/ROIAnalysisChart';
import SavingsDistributionChart from '../components/analytics/SavingsDistributionChart';
import FinancialPerformanceStatusChart from '../components/analytics/FinancialPerformanceStatusChart';
import CostBenefitAnalysisChart from '../components/analytics/CostBenefitAnalysisChart';
import MonthlySavingsForecastChart from '../components/analytics/MonthlySavingsForecastChart';

const emptyAnalyticsOverview = (): AnalyticsOverview => ({
  total_workshops: 0,
  completed_workshops: 0,
  ongoing_workshops: 0,
  planned_workshops: 0,
  passive_workshops: 0,
  completion_rate: 0,
  status_counts: {},
  total_estimated_savings: 0,
  total_released_savings: 0,
  realization_rate: 0,
  overdue_workshops: 0,
  upcoming_deadlines: 0,
  new_workshops_30d: 0,
  completions_30d: 0,
});

const emptyWorkshopPerformance = (): WorkshopPerformance => ({
  average_progress: 0,
  progress_distribution: {},
  average_completion_time: 0,
  velocity_trend: [],
  performance_by_status: {},
  performance_by_category: {},
  performance_by_department: {},
});

const emptyFinancialAnalytics = (): FinancialAnalytics => ({
  total_estimated_savings: 0,
  total_released_savings: 0,
  realization_rate: 0,
  monthly_savings_trend: [],
  savings_by_cc_category: {},
  savings_by_sqdcm_category: {},
  savings_by_workstream: {},
  average_roi: 0,
  top_performing_workshops: [],
});

const Analytics: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'overview' | 'performance' | 'financial' | 'teams' | 'departments' | 'plants' | 'insights' | 'export'>('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [datasetErrors, setDatasetErrors] = useState<Record<string, string>>({});
  const [data, setData] = useState<{
    overview: AnalyticsOverview;
    performance: WorkshopPerformance;
    financial: FinancialAnalytics;
  } | null>(null);
  const [teamData, setTeamData] = useState<TeamPerformance | null>(null);
  // Phase 3 data states
  const [temporalData, setTemporalData] = useState<TemporalAnalytics | null>(null);
  const [predictiveData, setPredictiveData] = useState<PredictiveAnalytics | null>(null);
  const [sqdcmData, setSQDCMData] = useState<SQDCMAnalytics | null>(null);
  const [maturityData, setMaturityData] = useState<APSMaturity | null>(null);
  const [exportJobs, setExportJobs] = useState<any[]>([]);
  const [reportTemplates, setReportTemplates] = useState<any[]>([]);
  const [scheduledReports, setScheduledReports] = useState<any[]>([]);
  const [dashboardLayout, setDashboardLayout] = useState<any>(null);
  const [departmentChartsData, setDepartmentChartsData] = useState<DepartmentChartsData | null>(null);
  const [teamChartsData, setTeamChartsData] = useState<TeamChartsData | null>(null);
  const [plantChartsData, setPlantChartsData] = useState<PlantChartsData | null>(null);
  const [insightsChartsData, setInsightsChartsData] = useState<InsightsChartsData | null>(null);
  const [filters, setFilters] = useState<AnalyticsFilters>({ period: 'all' });
  const [showFilters, setShowFilters] = useState(false);
  // Keep tab requests idempotent when React Strict Mode replays effects or a
  // user clicks between tabs before the previous request has completed.
  const inFlightDatasets = useRef<Set<string>>(new Set());
  const initialLoadStarted = useRef(false);
  const pendingLoads = useRef(0);

  const beginLoading = () => {
    pendingLoads.current += 1;
    setLoading(true);
  };

  const endLoading = () => {
    pendingLoads.current = Math.max(0, pendingLoads.current - 1);
    if (pendingLoads.current === 0) setLoading(false);
  };

  // Initialize from URL parameters
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab && ['overview', 'performance', 'financial', 'teams', 'departments', 'plants', 'insights', 'export'].includes(tab)) {
      setActiveTab(tab as any);
    }

    // Check if we're returning from a workshop detail page
    const storedReturnUrl = sessionStorage.getItem('workshopReturnUrl');
    if (storedReturnUrl && storedReturnUrl.includes('/analytics')) {
      // Clear the stored URL since we're already on the analytics page
      sessionStorage.removeItem('workshopReturnUrl');
    }

    // Handle browser back navigation to preserve filter state
    const handlePopState = (event: PopStateEvent) => {
      // If we're on analytics page and there's a return URL in sessionStorage, use it
      const returnUrl = sessionStorage.getItem('workshopReturnUrl');
      if (returnUrl && returnUrl.includes('/analytics')) {
        // Parse the return URL and update the current URL
        const url = new URL(returnUrl, window.location.origin);
        const newSearchParams = new URLSearchParams(url.search);
        
        // Update the current URL with the return URL parameters
        setSearchParams(newSearchParams);
        
        // Clear the stored URL
        sessionStorage.removeItem('workshopReturnUrl');
      }
    };

    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };

  
  }, [searchParams]);

  // Update URL when tab changes
  const handleTabChange = (tab: 'overview' | 'performance' | 'financial' | 'teams' | 'departments' | 'plants' | 'insights' | 'export') => {
    setActiveTab(tab);
    
    // Preserve existing URL parameters and update tab
    const currentParams = new URLSearchParams(searchParams);
    currentParams.set('tab', tab);
    setSearchParams(currentParams);
    
  };

  // Update URL when filters change
  const updateURLWithFilters = (newFilters: AnalyticsFilters) => {
    const params = new URLSearchParams();
    
    // Always include the current tab
    params.set('tab', activeTab);
    
    // Add filter parameters to URL
    Object.entries(newFilters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        if (Array.isArray(value)) {
          // Only add non-empty array values
          const nonEmptyValues = value.filter(item => item !== '' && item !== null && item !== undefined);
          if (nonEmptyValues.length > 0) {
            nonEmptyValues.forEach(item => params.append(key, item.toString()));
          }
        } else {
          params.set(key, value.toString());
        }
      }
    });
    
    setSearchParams(params);
  };

  const loadBaseAnalytics = async () => {
    const results = await Promise.allSettled([
      analyticsService.getOverview(filters),
      analyticsService.getWorkshopPerformance(filters),
      analyticsService.getFinancialAnalytics(filters),
    ]);
    const [overviewResult, performanceResult, financialResult] = results;
    const failures: Record<string, string> = {};
    if (overviewResult.status === 'rejected') failures.overview = 'Overview data could not be loaded.';
    if (performanceResult.status === 'rejected') failures.performance = 'Performance data could not be loaded.';
    if (financialResult.status === 'rejected') failures.financial = 'Financial data could not be loaded.';

    setData({
      overview: overviewResult.status === 'fulfilled' ? overviewResult.value : emptyAnalyticsOverview(),
      performance: performanceResult.status === 'fulfilled' ? performanceResult.value : emptyWorkshopPerformance(),
      financial: financialResult.status === 'fulfilled' ? financialResult.value : emptyFinancialAnalytics(),
    });
    setDatasetErrors(previous => {
      const next = { ...previous };
      for (const key of ['overview', 'performance', 'financial']) {
        if (!failures[key]) delete next[key];
      }
      return { ...next, ...failures };
    });
    return { failures, hasData: results.some(result => result.status === 'fulfilled') };
  };

  const fetchAnalytics = async () => {
    try {
      beginLoading();
      setError(null);
      const result = await loadBaseAnalytics();
      setError(result.hasData ? null : 'No analytics datasets could be loaded.');
    } catch (err) {
      setError('Failed to load analytics data. Please try again.');
      console.error('Analytics fetch error:', err);
    } finally {
      endLoading();
    }
  };

  const fetchTeamData = async () => {
    try {
      beginLoading();
      setError(null);
      const teamAnalyticsData = await analyticsService.getTeamPerformance(filters);
      setTeamData(teamAnalyticsData);
      setDatasetErrors(previous => {
        const next = { ...previous };
        delete next.teams;
        return next;
      });
    } catch (err) {
      setDatasetErrors(previous => ({ ...previous, teams: 'Team analytics data could not be loaded.' }));
      console.error('Team analytics fetch error:', err);
    } finally {
      endLoading();
    }
  };

  // Phase 3 fetch functions
  const fetchTemporalData = async () => {
    try {
      beginLoading();
      setError(null);
      const temporalAnalyticsData = await analyticsService.getTemporalAnalytics(filters);
      setTemporalData(temporalAnalyticsData);
      setDatasetErrors(previous => {
        const next = { ...previous };
        delete next.trends;
        return next;
      });
    } catch (err) {
      setDatasetErrors(previous => ({ ...previous, trends: 'Trend data could not be loaded.' }));
      console.error('Temporal analytics fetch error:', err);
    } finally {
      endLoading();
    }
  };

  const fetchPredictiveData = async () => {
    try {
      beginLoading();
      setError(null);
      const predictiveAnalyticsData = await analyticsService.getPredictiveAnalytics(filters);
      setPredictiveData(predictiveAnalyticsData);
      setDatasetErrors(previous => {
        const next = { ...previous };
        delete next.predictive;
        return next;
      });
    } catch (err) {
      setDatasetErrors(previous => ({ ...previous, predictive: 'Predictive analytics data could not be loaded.' }));
      console.error('Predictive analytics fetch error:', err);
    } finally {
      endLoading();
    }
  };

  const fetchSQDCMData = async () => {
    try {
      beginLoading();
      setError(null);
      const sqdcmAnalyticsData = await analyticsService.getSQDCMAnalytics(filters);
      setSQDCMData(sqdcmAnalyticsData);
      setDatasetErrors(previous => {
        const next = { ...previous };
        delete next.sqdcm;
        return next;
      });
    } catch (err) {
      setDatasetErrors(previous => ({ ...previous, sqdcm: 'SQDCM analytics data could not be loaded.' }));
      console.error('SQDCM analytics fetch error:', err);
    } finally {
      endLoading();
    }
  };

  const fetchDepartmentChartsData = async () => {
    try {
      beginLoading();
      setError(null);
      const departmentChartsData = await analyticsService.getDepartmentCharts(filters);
      setDepartmentChartsData(departmentChartsData);
      setDatasetErrors(previous => {
        const next = { ...previous };
        delete next.departments;
        return next;
      });
    } catch (err) {
      setDatasetErrors(previous => ({ ...previous, departments: 'Department analytics data could not be loaded.' }));
      console.error('Department charts fetch error:', err);
    } finally {
      endLoading();
    }
  };

  const fetchTeamChartsData = async () => {
    try {
      beginLoading();
      setError(null);
      const teamChartsData = await analyticsService.getTeamCharts(filters);
      setTeamChartsData(teamChartsData);
      setDatasetErrors(previous => {
        const next = { ...previous };
        delete next.teamCharts;
        return next;
      });
    } catch (err) {
      setDatasetErrors(previous => ({ ...previous, teamCharts: 'Team charts data could not be loaded.' }));
      console.error('Team charts fetch error:', err);
    } finally {
      endLoading();
    }
  };

  const fetchPlantChartsData = async () => {
    try {
      beginLoading();
      setError(null);
      const plantChartsData = await analyticsService.getPlantCharts(filters);
      setPlantChartsData(plantChartsData);
      setDatasetErrors(previous => {
        const next = { ...previous };
        delete next.plants;
        return next;
      });
    } catch (err) {
      setDatasetErrors(previous => ({ ...previous, plants: 'Plant analytics data could not be loaded.' }));
      console.error('Plant charts fetch error:', err);
    } finally {
      endLoading();
    }
  };

  const fetchMaturityData = async () => {
    try {
      beginLoading();
      setError(null);
      const maturityAnalyticsData = await analyticsService.getAPSMaturity(filters);
      setMaturityData(maturityAnalyticsData);
      setDatasetErrors(previous => {
        const next = { ...previous };
        delete next.maturity;
        return next;
      });
    } catch (err) {
      setDatasetErrors(previous => ({ ...previous, maturity: 'APS maturity data could not be loaded.' }));
      console.error('APS maturity fetch error:', err);
    } finally {
      endLoading();
    }
  };

  const fetchInsightsChartsData = async () => {
    try {
      beginLoading();
      setError(null);
      const insightsChartsData = await analyticsService.getInsightsCharts(filters);
      setInsightsChartsData(insightsChartsData);
      setDatasetErrors(previous => {
        const next = { ...previous };
        delete next.insights;
        return next;
      });
    } catch (err) {
      setDatasetErrors(previous => ({ ...previous, insights: 'Insights data could not be loaded.' }));
      console.error('Insights charts fetch error:', err);
    } finally {
      endLoading();
    }
  };

  const loadTabData = async (tab: typeof activeTab, force = false) => {
    const requests: Array<[string, boolean, () => Promise<void>]> = [
      ['temporal', tab === 'overview', fetchTemporalData],
      ['predictive', tab === 'performance', fetchPredictiveData],
      ['sqdcm', tab === 'financial', fetchSQDCMData],
      // The departments tab still renders the existing benchmarking card,
      // which is supplied by the team-performance endpoint.
      ['teams', tab === 'teams' || tab === 'departments', fetchTeamData],
      ['teamCharts', tab === 'teams', fetchTeamChartsData],
      ['maturity', tab === 'teams', fetchMaturityData],
      ['departments', tab === 'departments', fetchDepartmentChartsData],
      ['plants', tab === 'plants', fetchPlantChartsData],
      ['insights', tab === 'insights', fetchInsightsChartsData],
    ];

    await Promise.all(requests.filter(([, needed]) => needed).map(async ([key, , loader]) => {
      const loaded = key === 'temporal' ? temporalData
        : key === 'predictive' ? predictiveData
        : key === 'sqdcm' ? sqdcmData
        : key === 'teams' ? teamData
        : key === 'teamCharts' ? teamChartsData
        : key === 'maturity' ? maturityData
        : key === 'departments' ? departmentChartsData
        : key === 'plants' ? plantChartsData
        : insightsChartsData;
      if (!force && loaded) return;
      if (inFlightDatasets.current.has(key)) return;
      inFlightDatasets.current.add(key);
      try {
        await loader();
      } finally {
        inFlightDatasets.current.delete(key);
      }
    }));
  };

  // The active tab is the only non-overview dataset loaded on entry.  This
  // also handles deep links such as /analytics?tab=plants.
  useEffect(() => {
    void loadTabData(activeTab);
  }, [activeTab]);

  // Initial data load on component mount
  useEffect(() => {
    if (initialLoadStarted.current) return;
    initialLoadStarted.current = true;
    const loadInitialData = async () => {
      try {
        beginLoading();
        setError(null);

        // Each dataset is independent.  A single 500 (for example, the
        // performance endpoint) must not hide successful overview/financial
        // data or block the rest of the page.
        const baseResult = await Promise.allSettled([loadBaseAnalytics()]).then(([result]) => result);
        if (baseResult.status === 'fulfilled' && !baseResult.value.hasData) {
          setError('No analytics datasets could be loaded.');
        }
      } catch (err) {
        setError('Failed to load analytics data. Please try again.');
        console.error('Analytics fetch error:', err);
      } finally {
        endLoading();
      }
    };
    
    loadInitialData();
  }, []);

  // Removed automatic filtering - now using Apply Filters button

  // REMOVED ALL AUTOMATIC API CALLS - Only manual Apply Filters button triggers requests

  // Removed duplicate useEffect hooks - data loading is handled above

  // REMOVED AUTOMATIC FILTERING TAB LOADING - Only manual Apply Filters button triggers requests

  // REMOVED AUTOMATIC EXPORT TAB LOADING - Only manual Apply Filters button triggers requests

  // REMOVED AUTOMATIC CUSTOMIZE TAB LOADING - Only manual Apply Filters button triggers requests

  const handleFilterChange = (newFilters: Partial<AnalyticsFilters>) => {
    const updatedFilters = { ...filters, ...newFilters };
    setFilters(updatedFilters);
    updateURLWithFilters(updatedFilters);
  };

  const handleApplyFilters = async () => {
    try {
      beginLoading();
      setError(null);
      
      // Update URL with current filters
      updateURLWithFilters(filters);
      
      // Apply filters to independent base datasets.
      await fetchAnalytics();
      
      // Re-fetch only the visible tab's datasets with the new filters.
      setTeamData(null);
      setTemporalData(null);
      setPredictiveData(null);
      setSQDCMData(null);
      setMaturityData(null);
      setDepartmentChartsData(null);
      setTeamChartsData(null);
      setPlantChartsData(null);
      setInsightsChartsData(null);
      await loadTabData(activeTab, true);
    } catch (err) {
      setError('Failed to apply filters. Please try again.');
      console.error('Filter application error:', err);
    } finally {
      endLoading();
    }
  };

  const handleClearFilters = () => {
    const clearedFilters: AnalyticsFilters = {
      period: '30d',
      start_date: '',
      end_date: '',
      departments: [],
      plants: [],
      statuses: [],
      min_progress: undefined,
      max_progress: undefined,
      min_savings: undefined,
      max_savings: undefined,
      mentors: []
    };
    setFilters(clearedFilters);
    
    // Update URL to only include the tab parameter
    setSearchParams({ tab: activeTab });
  };


  const fetchExportJobs = async () => {
    try {
      const jobs = await analyticsService.getExportJobs();
      setExportJobs(jobs);
    } catch (err) {
      console.error('Export jobs fetch error:', err);
    }
  };

  const fetchReportTemplates = async () => {
    try {
      const templates = await analyticsService.getReportTemplates();
      setReportTemplates(templates);
    } catch (err) {
      console.error('Report templates fetch error:', err);
    }
  };

  const fetchScheduledReports = async () => {
    try {
      const reports = await analyticsService.getScheduledReports();
      setScheduledReports(reports);
    } catch (err) {
      console.error('Scheduled reports fetch error:', err);
    }
  };

  const fetchDashboardLayout = async () => {
    try {
      const layout = await analyticsService.getDashboardLayout();
      setDashboardLayout(layout);
    } catch (err) {
      console.error('Dashboard layout fetch error:', err);
    }
  };

  const handleRefresh = async () => {
    await Promise.all([
      fetchAnalytics(),
      loadTabData(activeTab, true),
    ]);
  };

  if (loading && !data) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="p-6">
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
          <h3 className="text-lg font-semibold text-gray-800 mb-2">No Data Available</h3>
          <p className="text-gray-600">No analytics data found for the selected filters.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
        <h1 className="text-3xl font-bold text-gray-900">Analytics</h1>
        <p className="mt-2 text-gray-600">
          Performance metrics and insights for your KATA initiatives.
        </p>
      </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center space-x-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Filter className="h-4 w-4" />
              <span>Filters</span>
            </button>
            <button
              onClick={handleRefresh}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <RefreshCw className="h-4 w-4" />
              <span>Refresh</span>
            </button>
          </div>
        </div>
        {(error || Object.keys(datasetErrors).length > 0) && (
          <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800 flex items-center justify-between gap-4">
            <span>{error || 'Some analytics datasets could not be loaded. Available datasets remain visible.'}</span>
            <button onClick={handleRefresh} className="underline font-medium whitespace-nowrap">Retry</button>
          </div>
        )}
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="mb-6 bg-white rounded-lg shadow-sm border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Filters</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Time Period</label>
              <select
                value={filters.period || '30d'}
                onChange={(e) => handleFilterChange({ period: e.target.value as any })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="7d">Last 7 days</option>
                <option value="30d">Last 30 days</option>
                <option value="90d">Last 90 days</option>
                <option value="1y">Last year</option>
                <option value="all">All time</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
              <input
                type="date"
                value={filters.start_date || ''}
                onChange={(e) => handleFilterChange({ start_date: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
              <input
                type="date"
                value={filters.end_date || ''}
                onChange={(e) => handleFilterChange({ end_date: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          {/* Filter Action Buttons */}
          <div className="mt-4 flex justify-end space-x-3">
            <button
              onClick={handleClearFilters}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center space-x-2"
            >
              <span>Clear Filters</span>
            </button>
            <button
              onClick={handleApplyFilters}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
            >
              <Filter className="h-4 w-4" />
              <span>Apply Filters</span>
            </button>
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => handleTabChange('overview')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'overview'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <BarChart3 className="h-4 w-4" />
                <span>Overview</span>
              </div>
            </button>
            <button
              onClick={() => handleTabChange('performance')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'performance'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <TrendingUp className="h-4 w-4" />
                <span>Performance</span>
              </div>
            </button>
            <button
              onClick={() => handleTabChange('financial')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'financial'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <Target className="h-4 w-4" />
                <span>Financial</span>
              </div>
            </button>
            <button
              onClick={() => handleTabChange('teams')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'teams'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <Users className="h-4 w-4" />
                <span>Teams</span>
              </div>
            </button>
            <button
              onClick={() => handleTabChange('departments')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'departments'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <Building2 className="h-4 w-4" />
                <span>Departments</span>
              </div>
            </button>
            <button
              onClick={() => handleTabChange('plants')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'plants'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <Globe className="h-4 w-4" />
                <span>Plants</span>
              </div>
            </button>
            <button
              onClick={() => handleTabChange('insights')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'insights'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <Lightbulb className="h-4 w-4" />
                <span>Insights</span>
              </div>
            </button>
            <button
              onClick={() => handleTabChange('export')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'export'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <Target className="h-4 w-4" />
                <span>Export</span>
              </div>
            </button>
          </nav>
        </div>
        </div>
        
      {/* Tab Content */}
      <ChartErrorBoundary
        key={activeTab}
        fallbackMessage="This analytics tab could not be displayed."
      >
      <div className="space-y-6">
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <LoadingSpinner />
              </div>
            ) : datasetErrors.overview ? (
              <div className="text-center py-8">
                <p className="text-red-600">{datasetErrors.overview}</p>
                <button
                  onClick={() => void fetchAnalytics()}
                  className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Retry
                </button>
              </div>
            ) : error ? (
              <div className="text-center py-8">
                <p className="text-red-600">{error}</p>
                <button 
                  onClick={() => fetchAnalytics()}
                  className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Retry
                </button>
              </div>
            ) : data ? (
          <div className="space-y-6">
            <AnalyticsOverviewComponent data={data.overview} loading={loading} />
            
            {/* New Charts Section */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <CompletionRatesChart 
                data={data.overview.completion_rates || []} 
                loading={loading} 
              />
              <StatusDistributionChart 
                data={data.overview.status_distribution || []} 
                loading={loading} 
              />
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <ProgressVelocityChart 
                data={data.overview.progress_velocity || []} 
                loading={loading} 
              />
              <SavingsTrendChart 
                data={data.overview.savings_trends || []} 
                loading={loading} 
              />
            </div>
            
            <DepartmentPerformanceChart 
              data={data.overview.department_performance || []} 
              loading={loading} 
            />

            {datasetErrors.trends && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                {datasetErrors.trends}
              </div>
            )}
            
            {temporalData && (
              <>
                <TrendsChart 
                  data={temporalData.monthly_trends} 
                  title="Monthly Workshop Trends" 
                  color="#3b82f6"
                />
                <SeasonalPatterns 
                  seasonalPatterns={temporalData.seasonal_patterns}
                  title="Seasonal Performance Patterns"
                />
              </>
            )}
          </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-500">No overview data available</p>
              </div>
            )}
          </div>
        )}
        {activeTab === 'performance' && (
          <div className="space-y-6">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <LoadingSpinner />
              </div>
            ) : datasetErrors.performance ? (
              <div className="text-center py-8">
                <p className="text-red-600">{datasetErrors.performance}</p>
                <button
                  onClick={() => void fetchAnalytics()}
                  className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Retry
                </button>
              </div>
            ) : error ? (
              <div className="text-center py-8">
                <p className="text-red-600">{error}</p>
                <button 
                  onClick={() => fetchAnalytics()}
                  className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Retry
                </button>
              </div>
            ) : data ? (
          <div className="space-y-6">
            <WorkshopPerformanceComponent data={data.performance} loading={loading} />
            {datasetErrors.predictive ? (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                {datasetErrors.predictive}
              </div>
            ) : null}
            
            {/* Performance Charts Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <ProgressDistributionChart 
                data={data.overview?.progress_distribution || {}} 
                loading={loading} 
              />
              <PerformanceByStatusChart 
                data={data.overview?.performance_by_status || []} 
                loading={loading} 
              />
            </div>
            
            <VelocityTrendChart 
              data={data.overview?.velocity_trend || []} 
              loading={loading} 
            />
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <RiskDistributionChart 
                data={data.overview?.risk_distribution || []} 
                loading={loading} 
              />
              <DepartmentPerformanceScatterChart 
                data={data.overview?.department_performance || []} 
                loading={loading} 
              />
            </div>
            
            <CategoryPerformanceChart 
              data={data.overview?.category_performance || []} 
              loading={loading} 
            />
            
            {predictiveData && (
              <RiskAssessmentGauge 
                riskAssessments={predictiveData.risk_assessments}
                title="Workshop Risk Assessment"
              />
            )}
          </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-500">No performance data available</p>
              </div>
            )}
          </div>
        )}
        {activeTab === 'financial' && (
          <div className="space-y-6">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <LoadingSpinner />
              </div>
            ) : datasetErrors.financial ? (
              <div className="text-center py-8">
                <p className="text-red-600">{datasetErrors.financial}</p>
                <button
                  onClick={() => void fetchAnalytics()}
                  className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Retry
                </button>
              </div>
            ) : error ? (
              <div className="text-center py-8">
                <p className="text-red-600">{error}</p>
                <button 
                  onClick={() => fetchAnalytics()}
                  className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Retry
                </button>
              </div>
            ) : data ? (
          <div className="space-y-6">
            <FinancialAnalyticsComponent data={data.financial} loading={loading} />
            {datasetErrors.sqdcm ? (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                {datasetErrors.sqdcm}
              </div>
            ) : null}
            
            {/* Financial Charts Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <SavingsTrendChart 
                data={data.overview?.savings_trend || []} 
                loading={loading} 
              />
              <ROIAnalysisChart 
                data={data.overview?.roi_analysis || []} 
                loading={loading} 
              />
            </div>
            
            <SavingsDistributionChart 
              data={data.overview?.savings_distribution || []} 
              loading={loading} 
            />
            
            <FinancialPerformanceStatusChart 
              data={data.overview?.financial_performance_status || []} 
              loading={loading} 
            />
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <CostBenefitAnalysisChart 
                data={data.overview?.cost_benefit_analysis || []} 
                loading={loading} 
              />
              <MonthlySavingsForecastChart 
                data={data.overview?.monthly_savings_forecast || []} 
                loading={loading} 
              />
            </div>
            
            {sqdcmData && (
              <SQDCMRadarChart 
                data={sqdcmData}
                title="SQDCM Category Performance Analysis"
              />
            )}
          </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-500">No financial data available</p>
              </div>
            )}
          </div>
        )}
        {activeTab === 'teams' && (
          <div className="space-y-6">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <LoadingSpinner />
              </div>
            ) : datasetErrors.teamCharts || datasetErrors.teams ? (
              <div className="text-center py-8">
                <p className="text-red-600">{datasetErrors.teamCharts || datasetErrors.teams}</p>
                <button
                  onClick={() => void loadTabData('teams', true)}
                  className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Retry
                </button>
              </div>
            ) : !teamChartsData ? (
              <div className="flex items-center justify-center py-12">
                <LoadingSpinner />
              </div>
            ) : error ? (
              <div className="text-center py-8">
                <p className="text-red-600">{error}</p>
                <button 
                  onClick={() => fetchTeamChartsData()}
                  className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Retry
                </button>
              </div>
            ) : teamChartsData ? (
              <>
                {datasetErrors.maturity ? (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                    {datasetErrors.maturity}
                  </div>
                ) : null}
                {/* Existing team components */}
                {teamData && (
                  <>
                    <MentorPerformanceComponent data={teamData.mentor_performance} loading={loading} />
                    <TeamCollaborationComponent 
                      data={teamData.team_collaboration} 
                      crossDepartmentInsights={teamData.cross_department_insights}
                      loading={loading} 
                    />
                    {maturityData && (
                      <MaturityProgressionChart 
                        data={maturityData}
                        title="APS Maturity Progression"
                      />
                    )}
                  </>
                )}
                
                {/* Team Performance Comparison Chart */}
                <TeamPerformanceComparisonChart 
                  data={teamChartsData.performance_comparison} 
                  loading={loading} 
                />
                
                {/* Mentor Effectiveness Analysis Chart */}
                <MentorEffectivenessAnalysisChart 
                  data={teamChartsData.mentor_effectiveness} 
                  loading={loading} 
                />
                
                {/* Team Performance Trends Chart */}
                <TeamPerformanceTrendsChart 
                  data={teamChartsData.performance_trends.data} 
                  teams={teamChartsData.performance_trends.teams}
                  loading={loading} 
                />
                
                {/* Team Workload Distribution Chart */}
                <TeamWorkloadDistributionChart 
                  data={teamChartsData.workload_distribution} 
                  loading={loading} 
                />
                
                {/* Team Knowledge Sharing Matrix Chart */}
                <TeamKnowledgeSharingMatrixChart 
                  data={teamChartsData.knowledge_sharing_matrix.data} 
                  teams={teamChartsData.knowledge_sharing_matrix.teams}
                  loading={loading} 
                />
                
                {/* Team Collaboration Network Chart */}
                <TeamCollaborationNetworkChart 
                  data={teamChartsData.collaboration_network} 
                  loading={loading} 
                />
              </>
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-500">No team data available</p>
              </div>
            )}
          </div>
        )}
        {activeTab === 'departments' && (
          <div className="space-y-6">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <LoadingSpinner />
              </div>
            ) : datasetErrors.departments ? (
              <div className="text-center py-8">
                <p className="text-red-600">{datasetErrors.departments}</p>
                <button
                  onClick={() => void loadTabData('departments', true)}
                  className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Retry
                </button>
              </div>
            ) : !departmentChartsData ? (
              <div className="flex items-center justify-center py-12">
                <LoadingSpinner />
              </div>
            ) : error ? (
              <div className="text-center py-8">
                <p className="text-red-600">{error}</p>
                <button 
                  onClick={() => fetchDepartmentChartsData()}
                  className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Retry
                </button>
              </div>
            ) : departmentChartsData ? (
              <>
                {/* Department Benchmarking (existing) */}
                {teamData && (
                  <DepartmentBenchmarking data={teamData.department_benchmarking} loading={loading} />
                )}
                
                {/* Department Performance Comparison Chart */}
                <DepartmentPerformanceComparisonChart 
                  data={departmentChartsData.performance_comparison} 
                  loading={loading} 
                />
                
                {/* Department Savings Analysis Chart */}
                <DepartmentSavingsAnalysisChart 
                  data={departmentChartsData.savings_analysis} 
                  loading={loading} 
                />
                
                {/* Department Progress Trends Chart */}
                <DepartmentProgressTrendsChart 
                  data={departmentChartsData.progress_trends.data} 
                  departments={departmentChartsData.progress_trends.departments}
                  loading={loading} 
                />
                
                {/* Department Workshop Distribution Chart */}
                <DepartmentWorkshopDistributionChart 
                  data={departmentChartsData.workshop_distribution} 
                  loading={loading} 
                />
                
                {/* Department Risk Assessment Chart */}
                <DepartmentRiskAssessmentChart 
                  data={departmentChartsData.risk_assessment} 
                  loading={loading} 
                />
                
                {/* Department Mentor Performance Chart */}
                <DepartmentMentorPerformanceChart 
                  data={departmentChartsData.mentor_performance} 
                  loading={loading} 
                />
              </>
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-500">No department data available</p>
              </div>
            )}
          </div>
        )}
        {activeTab === 'plants' && (
          <div className="space-y-6">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <LoadingSpinner />
              </div>
            ) : datasetErrors.plants ? (
              <div className="text-center py-8">
                <p className="text-red-600">{datasetErrors.plants}</p>
                <button
                  onClick={() => void loadTabData('plants', true)}
                  className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Retry
                </button>
              </div>
            ) : !plantChartsData ? (
              <div className="flex items-center justify-center py-12">
                <LoadingSpinner />
              </div>
            ) : error ? (
              <div className="text-center py-8">
                <p className="text-red-600">{error}</p>
                <button 
                  onClick={() => fetchPlantChartsData()}
                  className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Retry
                </button>
              </div>
            ) : plantChartsData ? (
              <>
                {/* Plant Performance Comparison Chart */}
                <PlantPerformanceComparisonChart 
                  data={plantChartsData.performance_comparison} 
                  loading={loading} 
                />
                
                {/* Plant Savings Analysis Chart */}
                <PlantSavingsAnalysisChart 
                  data={plantChartsData.savings_analysis} 
                  loading={loading} 
                />
                
                {/* Plant Progress Trends Chart */}
                <PlantProgressTrendsChart 
                  data={plantChartsData.progress_trends.data} 
                  plants={plantChartsData.progress_trends.plants}
                  loading={loading} 
                />
                
                {/* Plant Workshop Distribution Chart */}
                <PlantWorkshopDistributionChart 
                  data={plantChartsData.workshop_distribution} 
                  loading={loading} 
                />
                
                {/* Plant Risk Assessment Chart */}
                <PlantRiskAssessmentChart 
                  data={plantChartsData.risk_assessment} 
                  loading={loading} 
                />
                
                {/* Plant Mentor Performance Chart */}
                <PlantMentorPerformanceChart 
                  data={plantChartsData.mentor_performance} 
                  loading={loading} 
                />
              </>
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-500">No plant data available</p>
              </div>
            )}
          </div>
        )}
        {activeTab === 'insights' && (
          <div className="space-y-6">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <LoadingSpinner />
              </div>
            ) : datasetErrors.insights ? (
              <div className="text-center py-8">
                <p className="text-red-600">{datasetErrors.insights}</p>
                <button
                  onClick={() => void loadTabData('insights', true)}
                  className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Retry
                </button>
              </div>
            ) : !insightsChartsData ? (
              <div className="flex items-center justify-center py-12">
                <LoadingSpinner />
              </div>
            ) : error ? (
              <div className="text-center py-8">
                <p className="text-red-600">{error}</p>
                <button 
                  onClick={() => fetchInsightsChartsData()}
                  className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Retry
                </button>
              </div>
            ) : insightsChartsData ? (
              <>
                {/* Strategic Performance Dashboard */}
                <StrategicPerformanceDashboard 
                  data={insightsChartsData.strategic_performance} 
                  loading={loading} 
                />
                
                {/* Cross-Department Collaboration Matrix */}
                <CrossDepartmentCollaborationMatrix 
                  data={insightsChartsData.collaboration_matrix} 
                  loading={loading} 
                />
                
                {/* Predictive Analytics Dashboard */}
                <PredictiveAnalyticsDashboard 
                  data={insightsChartsData.predictive_analytics} 
                  loading={loading} 
                />
                
                {/* ROI & Business Impact Analysis */}
                <ROIBusinessImpactAnalysis 
                  data={insightsChartsData.roi_business_impact} 
                  loading={loading} 
                />
                
                {/* Performance Benchmarking Matrix */}
                <PerformanceBenchmarkingMatrix 
                  data={insightsChartsData.benchmarking_matrix} 
                  loading={loading} 
                />
                
                {/* Strategic Recommendations Engine */}
                <StrategicRecommendationsEngine 
                  data={insightsChartsData.recommendations_engine} 
                  loading={loading} 
                />
              </>
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-500">No insights data available</p>
              </div>
            )}
          </div>
        )}
        {activeTab === 'export' && (
          <div className="space-y-6">
            <ExportDashboard 
              onExport={async (exportData) => {
                try {
                  const result = await analyticsService.createExport(exportData);
                  setExportJobs(prev => [...prev, result]);
                  return result;
                } catch (error) {
                  console.error('Export error:', error);
                  throw error;
                }
              }}
              onGetExportJobs={async () => {
                try {
                  const jobs = await analyticsService.getExportJobs();
                  setExportJobs(jobs);
                  return jobs;
                } catch (error) {
                  console.error('Export jobs fetch error:', error);
                  return [];
                }
              }}
              onGetReportTemplates={async () => {
                try {
                  const templates = await analyticsService.getReportTemplates();
                  setReportTemplates(templates);
                  return templates;
                } catch (error) {
                  console.error('Report templates fetch error:', error);
                  return [];
                }
              }}
              onGetScheduledReports={async () => {
                try {
                  const reports = await analyticsService.getScheduledReports();
                  setScheduledReports(reports);
                  return reports;
                } catch (error) {
                  console.error('Scheduled reports fetch error:', error);
                  return [];
                }
              }}
            />
        </div>
        )}
      </div>
      </ChartErrorBoundary>
    </div>
  );
};

export default Analytics;
